import { logEvent } from '@/config/analytics';
import { buildRoomLink } from '@/config/app-config';
import type { Translator } from '@/i18n';

/** Звідки поділились — параметр `source`, як у мобільному клієнті. */
export type InviteSource = 'card' | 'menu';

/**
 * Скопіювати текст. Повертає повідомлення для показу: підтвердження, а якщо
 * буфер недоступний (немає жесту, немає дозволу) — сам текст, щоб його можна
 * було виділити вручну.
 */
async function copy(value: string, confirmation: string): Promise<string> {
  try {
    await navigator.clipboard.writeText(value);
    return confirmation;
  } catch {
    return value;
  }
}

/**
 * Поширити повідомлення-запрошення мовою інтерфейсу.
 *
 * Посилання вже всередині тексту, тож `url` окремо не передаємо — інакше
 * частина цілей дублює його. Без системного поширення (десктоп) або при
 * помилці — повідомлення в буфер. Скасування вікна — не помилка.
 *
 * Повертає `'shared'`, `'cancelled'` або повідомлення для показу (копіювання).
 */
export async function shareInvite(
  t: Translator['t'],
  roomId: string,
  source: InviteSource,
): Promise<string> {
  const message = t('inviteShareMessage', { link: buildRoomLink(roomId) });

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text: message });
      logEvent('share_room', { source });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Інша помилка — падаємо на копіювання нижче.
    }
  }

  logEvent('copy_room_link', { source });
  return copy(message, t('linkCopied'));
}

/** Скопіювати лише посилання. */
export async function copyInviteLink(
  t: Translator['t'],
  roomId: string,
  source: InviteSource,
): Promise<string> {
  logEvent('copy_room_link', { source });
  return copy(buildRoomLink(roomId), t('linkCopied'));
}

/** Скопіювати ID кімнати — запасний варіант, лише з панелі «Запросити». */
export async function copyRoomId(
  t: Translator['t'],
  roomId: string,
  source: InviteSource,
): Promise<string> {
  logEvent('copy_room_id', { source });
  return copy(roomId, t('idCopied'));
}
